/**
 * Searchlights at work (`world.hazards.searchlights`, 02 §9.2, 05 §7). The sim's lit zone is a
 * sector - `range` long, `halfAngle` either side of `angle` - and that exact sector is what is
 * drawn: a sparse dithered fan on the ground from the lamp outward, and a denser, brighter
 * pool over its far end. Both are self-lit and dither-thinned (no blending), draped over the
 * terrain, and only drawn after dark. The lamp's head turns to follow the beam by day too.
 */

import { type Material, Mesh, type Object3D, type Scene, type Texture, Vector3 } from 'three';
import { Pcg32 } from '../../sim/rng';
import type { Searchlight, World } from '../../sim/types';
import type { PixelCamera } from '../camera/PixelCamera';
import { searchlightLens } from '../kit/fieldworks';
import { MeshBuilder } from '../kit/MeshBuilder';
import { createDitherMaterial } from '../materials/dither';
import { createEmissiveMaterial } from '../materials/toon';
import type { Palette, TimeOfDay } from '../palette';

/** Rings of the fan from the lamp to the end of its reach, and wedges across it. */
const RINGS = 16;
const SEGS = 6;
/** The pool is the outer rings: the last 38 % of the reach. */
const POOL_FROM_RING = 10;
/** The fan starts this far from the pole: nearer, the light is still in the air. */
const INNER_RADIUS = 1.2;
/** Draped this far above the ground so slopes between samples do not swallow it. */
const LIFT = 0.12;
export const BEAM_COVERAGE = 0.25;
export const POOL_COVERAGE = 0.5625;
/** The head turns in this many notches, as figures do (05 §6.2). */
const HEAD_STEPS = 32;
/** Lights further than their reach plus this from the screen centre are not updated or drawn. */
const VIEW_MARGIN = 34;

export const beamsLit = (tod: TimeOfDay): boolean => tod === 'night' || tod === 'dusk';

/** Node (ring i, wedge j) of a fan whose lamp is at the origin, pointing along +z; ring 0 is the inner edge. */
export function fanNode(i: number, j: number, range: number, halfAngle: number, angle: number, out: Vector3): Vector3 {
  const d = INNER_RADIUS + ((Math.max(INNER_RADIUS, range) - INNER_RADIUS) * i) / RINGS;
  const a = angle + halfAngle * ((j / SEGS) * 2 - 1);
  return out.set(Math.sin(a) * d, 0, Math.cos(a) * d);
}

/** Part of the fan (rings `from`..`to`) as a mesh whose vertices are rewritten from shared grid nodes. */
class FanPart {
  readonly mesh: Mesh;
  private readonly nodeOf: number[] = [];

  constructor(from: number, to: number, material: Material, palette: Palette) {
    const mb = new MeshBuilder();
    const color = palette.color('fire.3');
    const v = (i: number, j: number): Vector3 => fanNode(i, j, 20, 0.3, 0, new Vector3());
    for (let i = from; i < to; i++) {
      for (let j = 0; j < SEGS; j++) {
        // Counter-clockwise seen from above, so the face - and its normal - look up.
        mb.quad(v(i, j), v(i + 1, j), v(i + 1, j + 1), v(i, j + 1), color);
        const n = (ri: number, rj: number): number => ri * (SEGS + 1) + rj;
        this.nodeOf.push(n(i, j), n(i + 1, j), n(i + 1, j + 1), n(i, j), n(i + 1, j + 1), n(i, j + 1));
      }
    }
    this.mesh = new Mesh(mb.build(), material);
    // Vertices move every frame: a bounding sphere computed once would cull the beam wrongly.
    this.mesh.frustumCulled = false;
    this.mesh.name = 'searchlight-beam';
  }

  write(nodes: Float32Array): void {
    const attr = this.mesh.geometry.getAttribute('position');
    for (let k = 0; k < this.nodeOf.length; k++) {
      const n = (this.nodeOf[k] ?? 0) * 3;
      attr.setXYZ(k, nodes[n] ?? 0, nodes[n + 1] ?? 0, nodes[n + 2] ?? 0);
    }
    attr.needsUpdate = true;
  }
}

interface LightView {
  beam: FanPart | null;
  pool: FanPart | null;
  lens: Mesh | null;
  head: Object3D | null;
}

export interface SearchlightOptions {
  timeOfDay: TimeOfDay;
  /** The lamp's turning part for a prop id (`StaticWorld.turningPart`); omit it and heads stay as placed. */
  head?: (propId: number) => Object3D | null;
}

export class SearchlightView {
  private readonly views = new Map<number, LightView>();
  private readonly lit: boolean;
  private readonly beamMaterial: Material;
  private readonly poolMaterial: Material;
  private readonly lensMaterial: Material;
  private readonly nodes = new Float32Array((RINGS + 1) * (SEGS + 1) * 3);
  private readonly scratch = new Vector3();

  constructor(
    private readonly scene: Scene,
    private readonly palette: Palette,
    gradient: Texture,
    private readonly groundY: (x: number, z: number) => number,
    private readonly options: SearchlightOptions,
  ) {
    this.lit = beamsLit(options.timeOfDay);
    const lift = { polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -4 };
    const dithered = (coverage: number, emissiveBoost: number): Material =>
      createDitherMaterial({ gradientMap: gradient, coverage: { value: coverage }, emissiveBoost });
    this.beamMaterial = dithered(BEAM_COVERAGE, 1.0);
    this.poolMaterial = dithered(POOL_COVERAGE, 1.3);
    Object.assign(this.beamMaterial, lift);
    Object.assign(this.poolMaterial, lift);
    this.lensMaterial = createEmissiveMaterial(gradient, 1.4);
  }

  private view(light: Searchlight): LightView {
    let v = this.views.get(light.propId);
    if (v) return v;
    v = { beam: null, pool: null, lens: null, head: this.options.head?.(light.propId) ?? null };
    if (this.lit) {
      v.beam = new FanPart(0, POOL_FROM_RING, this.beamMaterial, this.palette);
      v.pool = new FanPart(POOL_FROM_RING, RINGS, this.poolMaterial, this.palette);
      this.scene.add(v.beam.mesh, v.pool.mesh);
      if (v.head) {
        // The glow rides on the head, so it turns and is destroyed with it.
        v.lens = new Mesh(searchlightLens(this.palette, new Pcg32(1, 1)).build(), this.lensMaterial);
        v.head.add(v.lens);
      }
    }
    this.views.set(light.propId, v);
    return v;
  }

  /** Beams currently drawn (tests, debug overlay). */
  get beamsVisible(): number {
    let n = 0;
    for (const v of this.views.values()) if (v.beam?.mesh.visible) n++;
    return n;
  }

  update(world: World, camera: PixelCamera): void {
    for (const light of world.hazards.searchlights) {
      const v = this.view(light);
      const step = (Math.PI * 2) / HEAD_STEPS;
      // A destroyed prop's head has been taken out of the scene: leave it alone.
      if (v.head?.parent) v.head.rotation.y = Math.round(light.angle / step) * step;
      if (v.lens) v.lens.visible = light.on;
      if (!v.beam || !v.pool) continue;
      const near = Math.hypot(light.x - camera.focus.x, light.z - camera.focus.z) < light.range + VIEW_MARGIN;
      const shown = light.on && near;
      v.beam.mesh.visible = shown;
      v.pool.mesh.visible = shown;
      if (!shown) continue;
      for (let i = 0; i <= RINGS; i++) {
        for (let j = 0; j <= SEGS; j++) {
          const p = fanNode(i, j, light.range, light.halfAngle, light.angle, this.scratch);
          const x = light.x + p.x;
          const z = light.z + p.z;
          const n = (i * (SEGS + 1) + j) * 3;
          this.nodes[n] = x;
          this.nodes[n + 1] = this.groundY(x, z) + LIFT;
          this.nodes[n + 2] = z;
        }
      }
      v.beam.write(this.nodes);
      v.pool.write(this.nodes);
    }
  }

  dispose(): void {
    for (const v of this.views.values()) {
      for (const part of [v.beam, v.pool]) {
        if (!part) continue;
        this.scene.remove(part.mesh);
        part.mesh.geometry.dispose();
      }
      if (v.lens) {
        v.lens.removeFromParent();
        v.lens.geometry.dispose();
      }
    }
    this.views.clear();
    this.beamMaterial.dispose();
    this.poolMaterial.dispose();
    this.lensMaterial.dispose();
  }
}
